import React, { useState, useEffect } from 'react';
import { DatabaseService } from '../db/database';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth } from 'date-fns';
import './Calendar.css';

const STATUS_TYPES = {
  office: { label: 'Office', color: '#4caf50', icon: '🏢' },
  home: { label: 'Work from Home', color: '#2196f3', icon: '🏠' },
  annual_leave: { label: 'Annual Leave', color: '#ff9800', icon: '🌴' },
  public_holiday: { label: 'Public Holiday', color: '#9c27b0', icon: '🎉' }
};

function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [attendance, setAttendance] = useState({});
  const [holidays, setHolidays] = useState({});
  const [selectedStatus, setSelectedStatus] = useState('office');
  const [loading, setLoading] = useState(true);
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);

  useEffect(() => {
    loadMonthData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const loadMonthData = async () => {
    try {
      setLoading(true);

      if (!currentDate || !(currentDate instanceof Date) || isNaN(currentDate)) {
        console.error('Invalid currentDate:', currentDate);
        setLoading(false);
        return;
      }

      let start, end;
      try {
        start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
        end = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      } catch (err) {
        console.error('Error formatting dates:', err);
        setLoading(false);
        return;
      }

      // Load attendance records
      const attendanceRecords = await DatabaseService.getAttendanceRange(start, end);
      const attendanceMap = {};
      attendanceRecords.forEach(record => {
        attendanceMap[record.date] = record.status;
      });

      // Load holidays
      const year = currentDate.getFullYear();
      const holidayRecords = await DatabaseService.getHolidaysForYear(year);
      const holidayMap = {};
      holidayRecords.forEach(holiday => {
        holidayMap[holiday.date] = holiday.name;
      });

      // Load working days configuration
      const configuredWorkingDays = await DatabaseService.getWorkingDays();
      setWorkingDays(configuredWorkingDays);

      setAttendance(attendanceMap);
      setHolidays(holidayMap);
    } catch (error) {
      console.error('Error loading month data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = async (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');

    // Don't allow changing public holidays
    if (holidays[dateStr]) {
      return;
    }

    // Toggle or set status
    let newStatus;
    if (attendance[dateStr] === selectedStatus) {
      // If clicking same status, reset to default (home)
      newStatus = 'home';
    } else {
      newStatus = selectedStatus;
    }

    // Update database
    await DatabaseService.setAttendance(dateStr, newStatus);

    // Update local state
    setAttendance(prev => ({
      ...prev,
      [dateStr]: newStatus
    }));
  };

  const getDayStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');

    // Check if it's a public holiday
    if (holidays[dateStr]) {
      return 'public_holiday';
    }

    // If there's stored attendance, return it
    if (attendance[dateStr]) {
      return attendance[dateStr];
    }

    // For days without stored attendance, default to 'home' if it's a working day
    const dayOfWeek = date.getDay();
    const isWorking = workingDays.includes(dayOfWeek);

    // Non-working days (weekends by default) should not show as work from home
    // They will appear neutral unless explicitly marked
    return isWorking ? 'home' : null;
  };

  const getDayClassName = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const status = getDayStatus(date);
    const classes = ['calendar-day'];

    if (!isSameMonth(date, currentDate)) {
      classes.push('other-month');
    }

    const dayOfWeek = date.getDay();
    const isWorking = workingDays.includes(dayOfWeek);

    if (!isWorking) {
      classes.push('non-working-day');
    }

    if (holidays[dateStr]) {
      classes.push('holiday');
    }

    if (status) {
      classes.push(`status-${status}`);
    }

    return classes.join(' ');
  };

  const changeMonth = (delta) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  // Get all days in the month
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Add days from previous month to fill first week (Monday start)
  // getDay() returns 0 for Sunday, 1 for Monday, etc.
  // We want Monday (1) to be the first day, so we adjust
  let firstDayOfWeek = monthStart.getDay();
  firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Convert to Monday-based (0=Mon, 6=Sun)

  const daysFromPrevMonth = [];
  for (let i = 0; i < firstDayOfWeek; i++) {
    const date = new Date(monthStart);
    date.setDate(date.getDate() - (firstDayOfWeek - i));
    daysFromPrevMonth.push(date);
  }

  // Add days from next month to fill last week (Monday start)
  let lastDayOfWeek = monthEnd.getDay();
  lastDayOfWeek = lastDayOfWeek === 0 ? 6 : lastDayOfWeek - 1; // Convert to Monday-based

  const daysFromNextMonth = [];
  for (let i = 1; i < 7 - lastDayOfWeek; i++) {
    const date = new Date(monthEnd);
    date.setDate(date.getDate() + i);
    daysFromNextMonth.push(date);
  }

  const allDays = [...daysFromPrevMonth, ...daysInMonth, ...daysFromNextMonth];

  // Helper to check if a day is a working day
  const isWorkingDay = (date) => {
    return workingDays.includes(date.getDay());
  };

  // Calculate statistics for current month (only working days)
  const stats = Object.entries(STATUS_TYPES).reduce((acc, [key, value]) => {
    const count = daysInMonth.filter(date => {
      const status = getDayStatus(date);
      const isWorking = isWorkingDay(date);
      return status === key && isSameMonth(date, currentDate) && isWorking;
    }).length;
    acc[key] = count;
    return acc;
  }, {});

  // Calculate total working days in month (excluding annual leave and public holidays)
  const totalWorkingDays = daysInMonth.filter(date => {
    const isWorking = isWorkingDay(date);
    const status = getDayStatus(date);
    const isNonWorkingStatus = status === 'annual_leave' || status === 'public_holiday';
    return isWorking && isSameMonth(date, currentDate) && !isNonWorkingStatus;
  }).length;

  // Calculate percentages (office and home as percentage of actual working days)
  const percentages = Object.entries(stats).reduce((acc, [key, count]) => {
    // Only calculate percentages for office and home (actual work days)
    if (key === 'office' || key === 'home') {
      acc[key] = totalWorkingDays > 0 ? Math.round((count / totalWorkingDays) * 100) : 0;
    } else {
      // For annual_leave and public_holiday, show count but percentage relative to original working days
      const originalWorkingDays = daysInMonth.filter(date => {
        const isWorking = isWorkingDay(date);
        return isWorking && isSameMonth(date, currentDate);
      }).length;
      acc[key] = originalWorkingDays > 0 ? Math.round((count / originalWorkingDays) * 100) : 0;
    }
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="calendar-container">
        <div className="loading">Loading calendar...</div>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <div className="month-navigation">
          <button onClick={() => changeMonth(-1)} className="nav-btn">‹</button>
          <h2>{format(currentDate, 'MMMM yyyy')}</h2>
          <button onClick={() => changeMonth(1)} className="nav-btn">›</button>
        </div>
        <button onClick={goToToday} className="today-btn">Today</button>
      </div>

      <div className="status-selector">
        {Object.entries(STATUS_TYPES).map(([key, value]) => (
          <button
            key={key}
            className={`status-btn ${selectedStatus === key ? 'active' : ''}`}
            onClick={() => setSelectedStatus(key)}
            style={{
              borderColor: selectedStatus === key ? value.color : '#ddd',
              background: selectedStatus === key ? value.color + '20' : 'white'
            }}
          >
            <span className="status-icon">{value.icon}</span>
            <div className="status-info">
              <span className="status-label">{value.label}</span>
              <span className="status-stats">
                {stats[key]} days ({percentages[key]}%)
              </span>
            </div>
          </button>
        ))}
      </div>

      <div className="calendar-grid">
        <div className="calendar-weekdays">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>

        <div className="calendar-days">
          {allDays.map((date, index) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const status = getDayStatus(date);
            const statusInfo = status ? STATUS_TYPES[status] : null;

            // For non-working days without status, show minimal styling
            const backgroundColor = statusInfo ? statusInfo.color + '15' : '#f9f9f9';
            const borderColor = statusInfo ? statusInfo.color : '#e0e0e0';
            const icon = statusInfo ? statusInfo.icon : '';
            const title = holidays[dateStr] || (statusInfo ? statusInfo.label : 'Non-working day');

            return (
              <div
                key={index}
                className={getDayClassName(date)}
                onClick={() => handleDateClick(date)}
                title={title}
                style={{
                  backgroundColor: backgroundColor,
                  borderLeft: `4px solid ${borderColor}`
                }}
              >
                <span className="day-number">{format(date, 'd')}</span>
                {icon && <span className="day-icon">{icon}</span>}
                {holidays[dateStr] && (
                  <span className="holiday-name">{holidays[dateStr]}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}

export default Calendar;
